// src/components/Portfolio/MarketImpactDashboard.tsx
// Market Impact Dashboard - Connects climate risk to market indicators
// Part of Spatial Risk Q Platform

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ShowChart as ChartIcon,
  AccountBalance as BankIcon,
  LocalGasStation as GasIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  Lightbulb as LightbulbIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface MarketIndicator {
  id: string;
  name: string;
  value: number;
  change: number;
  changePercent: number;
  date: string;
  unit: string;
  category: string;
  trend: 'up' | 'down' | 'neutral';
}

interface HazardCorrelation {
  hazard: string;
  hazardLabel: string;
  commodities: string[];
  sectors: string[];
  indicators: string[];
  impactDescription: string;
  historicalImpact: string;
  hedgingStrategies: string[];
}

interface MarketAlert {
  type: 'warning' | 'info' | 'critical';
  title: string;
  message: string;
  indicator?: string;
  threshold?: number;
  currentValue?: number;
}

interface PortfolioMarketExposure {
  overallScore: number;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  correlatedSectors: string[];
  correlatedCommodities: string[];
  marketSensitivity: number;
  recommendations: string[];
  alerts: MarketAlert[];
}

interface PortfolioItem {
  postal_code?: string;
  asset_value?: number | string;
  hurricane?: number;
  flood?: number;
  wildfire?: number;
  earthquake?: number;
  drought?: number;
  heat?: number;
  winter?: number;
  tornado?: number;
  [key: string]: any;
}

interface MarketImpactDashboardProps {
  portfolio: {
    items: PortfolioItem[];
    name?: string;
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatCurrency = (value: number): string => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(2)}`;
};

const formatPercent = (value: number): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

const getAssetValue = (item: PortfolioItem): number => {
  if (!item.asset_value) return 0;
  if (typeof item.asset_value === 'number') return item.asset_value;
  return parseFloat(String(item.asset_value).replace(/[$,]/g, '')) || 0;
};

const getTrendIcon = (trend: string) => {
  switch (trend) {
    case 'up':
      return <TrendingUpIcon sx={{ color: '#10B981' }} />;
    case 'down':
      return <TrendingDownIcon sx={{ color: '#EF4444' }} />;
    default:
      return <TrendingFlatIcon sx={{ color: '#6B7280' }} />;
  }
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Energy':
      return <GasIcon />;
    case 'Rates':
      return <BankIcon />;
    case 'Volatility':
      return <ChartIcon />;
    default:
      return <TimelineIcon />;
  }
};

const getRiskLevelColor = (level: string): string => {
  switch (level) {
    case 'Critical':
      return '#EF4444';
    case 'High':
      return '#F59E0B';
    case 'Medium':
      return '#3B82F6';
    default:
      return '#10B981';
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MarketImpactDashboard: React.FC<MarketImpactDashboardProps> = ({ portfolio }) => {
  // State
  const [indicators, setIndicators] = useState<MarketIndicator[]>([]);
  const [correlations, setCorrelations] = useState<HazardCorrelation[]>([]);
  const [exposure, setExposure] = useState<PortfolioMarketExposure | null>(null);
  const [marketAlerts, setMarketAlerts] = useState<MarketAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCorrelation, setExpandedCorrelation] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Calculate portfolio hazard profile
  const portfolioHazards = useMemo(() => {
    if (!portfolio?.items?.length) return [];

    const hazardTypes = ['hurricane', 'flood', 'wildfire', 'earthquake', 'drought', 'heat', 'winter', 'tornado'];
    const hazardStats: { hazard: string; score: number; assetValue: number; count: number }[] = [];

    hazardTypes.forEach(hazard => {
      let totalScore = 0;
      let totalValue = 0;
      let count = 0;

      portfolio.items.forEach(item => {
        const score = parseFloat(item[hazard]) || 0;
        if (score > 3) { // Only count significant exposure
          totalScore += score;
          totalValue += getAssetValue(item);
          count++;
        }
      });

      if (count > 0) {
        hazardStats.push({
          hazard,
          score: totalScore / count,
          assetValue: totalValue,
          count
        });
      }
    });

    return hazardStats.sort((a, b) => b.score - a.score);
  }, [portfolio]);

  // Fetch market data
  const fetchMarketData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch indicators
      const indicatorsRes = await axios.get(`${API_URL}/market/indicators`);
      if (indicatorsRes.data.success) {
        setIndicators(indicatorsRes.data.data);
      }

      // Fetch correlations for portfolio's top hazards
      const topHazards = portfolioHazards.slice(0, 4).map(h => h.hazard);
      if (topHazards.length > 0) {
        const correlationsRes = await axios.get(`${API_URL}/market/correlations`, {
          params: { hazards: topHazards.join(',') }
        });
        if (correlationsRes.data.success) {
          setCorrelations(correlationsRes.data.data);
        }
      }

      // Calculate portfolio exposure
      if (portfolioHazards.length > 0) {
        const exposureRes = await axios.post(`${API_URL}/market/portfolio-exposure`, {
          hazards: portfolioHazards.map(h => ({
            hazard: h.hazard,
            score: h.score,
            assetValue: h.assetValue
          }))
        });
        if (exposureRes.data.success) {
          setExposure(exposureRes.data.data);
        }
      }

      // Fetch market alerts
      const alertsRes = await axios.get(`${API_URL}/market/alerts`);
      if (alertsRes.data.success) {
        setMarketAlerts(alertsRes.data.data);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching market data:', err);
      setError('Failed to fetch market data. Using cached data if available.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchMarketData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [portfolioHazards]);

  // ============================================================================
  // RENDER COMPONENTS
  // ============================================================================

  // Market Exposure Score Card
  const ExposureScoreCard = () => (
    <Card sx={{ height: '100%', border: exposure ? `2px solid ${getRiskLevelColor(exposure.riskLevel)}` : undefined }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
            Market Exposure Score
          </Typography>
          <Chip
            label={exposure?.riskLevel || 'Calculating...'}
            size="small"
            sx={{
              bgcolor: exposure ? getRiskLevelColor(exposure.riskLevel) : '#E5E7EB',
              color: 'white',
              fontWeight: 600
            }}
          />
        </Box>

        {exposure ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 700, color: getRiskLevelColor(exposure.riskLevel) }}>
                {exposure.overallScore}
              </Typography>
              <Typography variant="h5" sx={{ color: '#6B7280', ml: 1 }}>/100</Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={exposure.overallScore}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#E5E7EB',
                mb: 2,
                '& .MuiLinearProgress-bar': {
                  bgcolor: getRiskLevelColor(exposure.riskLevel),
                  borderRadius: 4
                }
              }}
            />

            <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
              Your portfolio's climate risk profile correlates with {exposure.correlatedSectors.length} market sectors
              and {exposure.correlatedCommodities.length} commodity markets.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {exposure.correlatedSectors.slice(0, 4).map((sector, idx) => (
                <Chip key={idx} label={sector} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              ))}
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Market Indicators Grid
  const IndicatorsGrid = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
            Key Market Indicators
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {lastUpdated && (
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Updated: {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}
            <IconButton size="small" onClick={fetchMarketData} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {indicators.map((indicator) => (
            <Grid item xs={6} sm={4} md={2} key={indicator.id}>
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: '#F9FAFB',
                  borderRadius: 2,
                  border: '1px solid #E5E7EB'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  {getCategoryIcon(indicator.category)}
                  <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
                    {indicator.name}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937' }}>
                  {indicator.value.toFixed(2)}
                  <Typography component="span" variant="caption" sx={{ ml: 0.5, color: '#9CA3AF' }}>
                    {indicator.unit}
                  </Typography>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {getTrendIcon(indicator.trend)}
                  <Typography
                    variant="caption"
                    sx={{
                      color: indicator.changePercent >= 0 ? '#10B981' : '#EF4444',
                      fontWeight: 600
                    }}
                  >
                    {formatPercent(indicator.changePercent)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  // Market Alerts Section
  const MarketAlertsSection = () => {
    const allAlerts = [...marketAlerts, ...(exposure?.alerts || [])];
    
    if (allAlerts.length === 0) return null;

    return (
      <Card sx={{ bgcolor: '#FEF3C7', border: '1px solid #F59E0B' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <WarningIcon sx={{ color: '#D97706' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#92400E' }}>
              Market Alerts ({allAlerts.length})
            </Typography>
          </Box>

          <List dense disablePadding>
            {allAlerts.slice(0, 5).map((alert, idx) => (
              <ListItem key={idx} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {alert.type === 'critical' ? (
                    <ErrorIcon sx={{ color: '#EF4444' }} />
                  ) : alert.type === 'warning' ? (
                    <WarningIcon sx={{ color: '#F59E0B' }} />
                  ) : (
                    <InfoIcon sx={{ color: '#3B82F6' }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                      {alert.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: '#78350F' }}>
                      {alert.message}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  // Hazard-Market Correlations
  const CorrelationsSection = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', mb: 2 }}>
          Climate-Market Correlations
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
          Based on your portfolio's hazard exposure, here are the relevant market correlations:
        </Typography>

        {correlations.map((correlation) => (
          <Paper
            key={correlation.hazard}
            sx={{
              mb: 2,
              border: '1px solid #E5E7EB',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                cursor: 'pointer',
                bgcolor: expandedCorrelation === correlation.hazard ? '#F3F4F6' : 'white',
                '&:hover': { bgcolor: '#F9FAFB' }
              }}
              onClick={() => setExpandedCorrelation(
                expandedCorrelation === correlation.hazard ? null : correlation.hazard
              )}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1F2937' }}>
                  {correlation.hazardLabel} Exposure
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  {correlation.commodities.slice(0, 3).map((c, idx) => (
                    <Chip key={idx} label={c} size="small" sx={{ fontSize: '0.65rem' }} />
                  ))}
                  {correlation.commodities.length > 3 && (
                    <Chip label={`+${correlation.commodities.length - 3}`} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
              {expandedCorrelation === correlation.hazard ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>

            <Collapse in={expandedCorrelation === correlation.hazard}>
              <Box sx={{ p: 2, pt: 0, bgcolor: '#F9FAFB' }}>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body2" sx={{ color: '#4B5563', mb: 2 }}>
                  {correlation.impactDescription}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280' }}>
                    HISTORICAL IMPACT
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1F2937', fontStyle: 'italic' }}>
                    {correlation.historicalImpact}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280' }}>
                    CORRELATED SECTORS
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {correlation.sectors.map((sector, idx) => (
                      <Chip key={idx} label={sector} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280' }}>
                    HEDGING STRATEGIES
                  </Typography>
                  <List dense disablePadding>
                    {correlation.hedgingStrategies.map((strategy, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <SecurityIcon fontSize="small" sx={{ color: '#10B981' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ color: '#1F2937' }}>
                              {strategy}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Collapse>
          </Paper>
        ))}
      </CardContent>
    </Card>
  );

  // Recommendations Section
  const RecommendationsSection = () => {
    if (!exposure?.recommendations?.length) return null;

    return (
      <Card sx={{ bgcolor: '#ECFDF5', border: '1px solid #10B981' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LightbulbIcon sx={{ color: '#059669' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#065F46' }}>
              Hedging Recommendations
            </Typography>
          </Box>

          <List dense disablePadding>
            {exposure.recommendations.map((rec, idx) => (
              <ListItem key={idx} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: '#10B981',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {idx + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ color: '#065F46' }}>
                      {rec}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  // Portfolio Hazard Breakdown
  const HazardBreakdown = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', mb: 2 }}>
          Portfolio Hazard Profile
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Hazard</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Score</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Locations</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Value Exposed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {portfolioHazards.slice(0, 8).map((hazard) => (
                <TableRow key={hazard.hazard}>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{hazard.hazard}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={hazard.score.toFixed(1)}
                      size="small"
                      sx={{
                        bgcolor: hazard.score >= 7 ? '#FEE2E2' : hazard.score >= 5 ? '#FEF3C7' : '#D1FAE5',
                        color: hazard.score >= 7 ? '#991B1B' : hazard.score >= 5 ? '#92400E' : '#065F46',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{hazard.count}</TableCell>
                  <TableCell align="right">{formatCurrency(hazard.assetValue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (loading && indicators.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" sx={{ color: '#6B7280' }}>
          Loading market data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
          Market Impact Analysis
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280' }}>
          Connecting your portfolio's climate risk exposure to real-time market indicators and hedging opportunities.
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Top Row: Score + Alerts */}
        <Grid item xs={12} md={4}>
          <ExposureScoreCard />
        </Grid>
        <Grid item xs={12} md={8}>
          <MarketAlertsSection />
          {(!marketAlerts.length && !exposure?.alerts?.length) && (
            <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <InfoIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 1 }} />
                <Typography variant="body1" sx={{ color: '#6B7280' }}>
                  No market alerts at this time
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Market Indicators */}
        <Grid item xs={12}>
          <IndicatorsGrid />
        </Grid>

        {/* Correlations and Hazard Breakdown */}
        <Grid item xs={12} md={8}>
          <CorrelationsSection />
        </Grid>
        <Grid item xs={12} md={4}>
          <HazardBreakdown />
        </Grid>

        {/* Recommendations */}
        <Grid item xs={12}>
          <RecommendationsSection />
        </Grid>
      </Grid>

      {/* Data Source Attribution */}
      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #E5E7EB' }}>
        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
          Market data provided by Federal Reserve Economic Data (FRED). Updated every 5 minutes.
          Correlations are based on historical analysis and should not be considered investment advice.
        </Typography>
      </Box>
    </Box>
  );
};

export default MarketImpactDashboard;
